import {
  IonList,
  IonItem,
  IonLabel,
  IonCard,
  IonCardContent,
  IonTitle,
  IonLoading,
  RefresherEventDetail,
  IonRefresher,
  IonRefresherContent,
} from "@ionic/react";
import { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryExec } from "../hooks/useQueryExec";
import { decodeParam, encodeParam } from "../util/ApiHelper";
import { formatData, getAllFields } from "../models/workorders/MeterReading";
import OnlineStatus from "./OnlineStatus";
import { TranslationsContext } from "../util/Translations";
import { useWorkOrderStore } from "../store/wo-order";
import { useShallow } from "zustand/react/shallow";
import useGetMeterMetadata from "../hooks/work-order/useGetMeterMetadata";
import useGetMeterReading from "../hooks/work-order/useGetMeterReading";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { useIonViewWillEnter } from "@ionic/react";

const Meters: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const { errorState } = useQueryExec(
    "Work Order",
    "Work Order",
    "cf_work_order"
  );
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const displayWo = decodeParam(workOrderId);

  const [showLoading, setShowLoading] = useState(false);
  const [inRefresh, setInRefresh] = useState(false);

  const metadata = useWorkOrderStore((state) => state.getMeterMetadataForWo());
  const workOrder = useWorkOrderStore(
    useShallow((state) => state.getWorkOrderById(displayWo))
  );
  const meterReading = useWorkOrderStore(
    useShallow((state) => state.getMeterReadingsForWo(displayWo))
  );
  const { getMeterMetadata } = useGetMeterMetadata();
  const { getMeterReading } = useGetMeterReading();

  const isSerial = workOrder?.WriteWoToSerialFlag === true ? "s" : "e";
  const equipSerial = workOrder?.Equipment;

  function handleRefresh(event: CustomEvent<RefresherEventDetail>) {
    setInRefresh(true);
    event.detail.complete();
  }

  const getMetersList = useCallback(async (forceRefresh= false) => {
    if (meterReading && !forceRefresh) return; //got it from cache
    await getMeterReading(displayWo);
  }, [displayWo, getMeterReading, meterReading]);

  useIonViewWillEnter(() => {
    // Force refresh the meter readings when entering this page
    getMetersList(true);
  });

  const metadataQuery = useCallback(async () => {
    if (metadata) return; //got it from the cache
    await getMeterMetadata();
  }, [getMeterMetadata, metadata]);

  useEffect(() => {
    (async () => {
      setShowLoading(true);
      await metadataQuery();
      await getMetersList();
      setShowLoading(false);
    })();
  }, [workOrderId, inRefresh, metadataQuery, getMetersList]);

  return (
    <>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={5000}
      />
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent></IonRefresherContent>
      </IonRefresher>
      <OnlineStatus />
      {isSerial === "s" ? (
        <IonTitle
          style={{ padding: "12px", margin: "5px", display: "inline-block", position: "sticky", top:0, zIndex: 10, backgroundColor: 'var(--ion-background-color)'}}
        >
          {`${
            translations["lbl_wologlabor_serialid"] || "Serial ID"
          }: ${equipSerial}`}
        </IonTitle>
      ) : (
        <IonTitle
          style={{ padding: "12px", margin: "5px", display: "inline-block", position: "sticky", top:0, zIndex: 10, backgroundColor: 'var(--ion-background-color)'}}
        >
          {translations["lbl_btn_equipment"] || "Equipment"}: {equipSerial}
        </IonTitle>
      )}
      {!!metadata &&
        !!meterReading &&
        meterReading.map((meter: any, index) => {
          return (
            <IonCard
              key={index}
              routerLink={`/workorder/${encodeParam(
                displayWo
              )}/meterReadingHistory/${encodeParam(meter.Meter)}`}
            >
              <IonCardContent>
                <IonList lines="full">
                  {getAllFields(metadata).map((meterField: any) => {
                    const data = meter[meterField.PropertyName];
                    return (
                      <IonItem key={meterField.IdText}>
                        <IonLabel>{meterField.TranslatedIdText}</IonLabel>
                        {formatData(data, meterField)}
                      </IonItem>
                    );
                  })}
                </IonList>
              </IonCardContent>
            </IonCard>
          );
        })}
      <IonLabel hidden={errorState === ""} color={"danger"}>
        {errorState}
      </IonLabel>
    </>
  );
};

export default withAITracking(reactPlugin, Meters, "Meters");
